/**
 * Portfolio EVM — EVM for any mix of jobs, programs, portfolios, and divisions.
 * Backend: GET /api/portfolio-evm (sums the per-job EVM; see README_PORTFOLIO_EVM.md).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { authenticatedApiCall } from '../../utils/apiClient';
import FilterDropdown, { FilterCheckList, FilterRadioList, pickCountSummary } from '../Reports/FilterDropdown';
import { ListenButton } from '../common/AudioGuide';
import PortfolioEvmHelpModal, { PORTFOLIO_EVM_GUIDE_ID } from './PortfolioEvmHelpModal';

const ACCENT = '#0f766e';
const SCOPE_KEY = 'portfolio-evm-scope-v1';

const btn = {
  padding: '6px 12px',
  fontSize: '0.8rem',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  background: 'white',
  color: '#334155',
  cursor: 'pointer',
};

const SERIES = {
  budget: { label: 'Budget (plan)', color: '#7dd3fc', key: 'budget' },
  earned: { label: 'Earned', color: '#15803d', key: 'earned' },
  actual: { label: 'Spent', color: '#dc2626', key: 'actual' },
  remaining: { label: 'Remaining', color: '#f59e0b', key: 'remaining' },
};

const MEMBER_PALETTE = [
  '#2563eb', '#16a34a', '#ea580c', '#9333ea', '#0891b2', '#dc2626', '#ca8a04', '#db2777',
  '#4f46e5', '#059669', '#b45309', '#7c3aed', '#0e7490', '#be123c', '#65a30d', '#c026d3',
];

const FLAG_TEXT = {
  earned_exceeds_budget: 'Earned is above budget — check the P6 proposal / Current pick on the job',
  no_budget: 'No P6 budget for this job',
  no_spent: 'No Maestro spent yet',
  no_earned: 'Spent but nothing earned',
  spi_out_of_range: 'SPI out of range',
  cpi_out_of_range: 'CPI out of range',
  stale_data_date: 'Data date is more than four weeks behind the latest job',
};

function fmtH(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-CA', { maximumFractionDigits: 0 });
}
function fmtIdx(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(2);
}
function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(1)}%`;
}
function fmtSigned(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  return `${v > 0 ? '+' : ''}${fmtH(v)}`;
}
function fmtDate(ymd) {
  if (!ymd) return '';
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(ymd);
  return d.toLocaleDateString('en-CA', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
}
function indexColor(v) {
  if (v == null) return '#64748b';
  if (v >= 1) return '#15803d';
  if (v >= 0.9) return '#b45309';
  return '#b91c1c';
}

function loadScope() {
  try {
    const raw = window.localStorage.getItem(SCOPE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return {
      jobs: Array.isArray(s.jobs) ? s.jobs : [],
      programs: Array.isArray(s.programs) ? s.programs : [],
      portfolios: Array.isArray(s.portfolios) ? s.portfolios : [],
      divisions: Array.isArray(s.divisions) ? s.divisions : [],
      includeUnawarded: !!s.includeUnawarded,
    };
  } catch (_) { return null; }
}
function saveScope(scope) {
  try { window.localStorage.setItem(SCOPE_KEY, JSON.stringify(scope)); } catch (_) { /* ignore */ }
}

function KpiCard({ label, value, sub, color, title }) {
  return (
    <div title={title} style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 10px', minWidth: 0,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: color || '#0f172a', lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function toggleIn(list, id) {
  const s = String(id);
  return list.map(String).includes(s) ? list.filter((x) => String(x) !== s) : [...list, s];
}

export default function PortfolioEvmPage() {
  const { instance, accounts } = useMsal();
  const [catalog, setCatalog] = useState({ programs: [], portfolios: [], divisions: [], jobs: [] });
  const [catalogError, setCatalogError] = useState(null);
  const [scope, setScope] = useState(() => loadScope() || { jobs: [], programs: [], portfolios: [], divisions: [], includeUnawarded: false });
  const [applied, setApplied] = useState(() => loadScope());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [series, setSeries] = useState('remaining');
  const [showHelp, setShowHelp] = useState(false);
  const [showTable, setShowTable] = useState(true);
  const [hidden, setHidden] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/portfolio-evm/catalog', null, { timeout: 60000 });
        if (!alive) return;
        const body = res?.data;
        if (body?.success) setCatalog({ programs: body.programs || [], portfolios: body.portfolios || [], divisions: body.divisions || [], jobs: body.jobs || [] });
        else if (body?.message) setCatalogError(body.message);
      } catch (e) {
        if (alive) setCatalogError(e.message || 'Could not load the scope lists');
      }
    })();
    return () => { alive = false; };
  }, [instance, accounts]);

  const scopeEmpty = !scope.jobs.length && !scope.programs.length && !scope.portfolios.length && !scope.divisions.length;

  const load = useCallback(async (sel, { refresh = false } = {}) => {
    if (!sel) return;
    const qs = new URLSearchParams();
    if (sel.jobs.length) qs.set('jobs', sel.jobs.join(','));
    if (sel.programs.length) qs.set('programs', sel.programs.join(','));
    if (sel.portfolios.length) qs.set('portfolios', sel.portfolios.join(','));
    if (sel.divisions.length) qs.set('divisions', sel.divisions.join(','));
    if (sel.includeUnawarded) qs.set('include_unawarded', '1');
    if (refresh) qs.set('refresh', '1');
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/portfolio-evm?${qs.toString()}`, null, { timeout: 300000 });
      const body = res?.data;
      if (body?.success) {
        setData(body);
        setHidden(new Set());
      } else {
        setError(body?.message || body?.error || 'Portfolio EVM failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Portfolio EVM failed');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (applied && (applied.jobs.length || applied.programs.length || applied.portfolios.length || applied.divisions.length)) {
      load(applied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (refresh = false) => {
    if (scopeEmpty) return;
    saveScope(scope);
    setApplied(scope);
    load(scope, { refresh });
  };

  const okMembers = useMemo(() => (data?.members || []).filter((m) => m.status === 'ok'), [data]);
  const otherMembers = useMemo(() => (data?.members || []).filter((m) => m.status !== 'ok'), [data]);
  const memberColor = useMemo(() => {
    const map = {};
    okMembers.forEach((m, i) => { map[m.key] = MEMBER_PALETTE[i % MEMBER_PALETTE.length]; });
    return map;
  }, [okMembers]);

  const chartRows = useMemo(() => {
    const rows = data?.weekly || [];
    const key = SERIES[series].key;
    return rows.map((w) => {
      const row = {
        date: w.date,
        budget_cumulative: w.budget_cumulative,
        earned_cumulative: w.earned_cumulative,
        actual_cumulative: w.actual_cumulative,
        forecast_cumulative: w.forecast_cumulative,
      };
      okMembers.forEach((m) => {
        row[m.key] = Math.round(((w.members || {})[m.key]?.[key] || 0));
      });
      return row;
    });
  }, [data, series, okMembers]);

  const pmi = data?.pmiSummary || null;
  const closedWeek = data?.meta?.closedWeek || null;

  const scopeLabel = useMemo(() => {
    if (!data?.scope) return '';
    const bits = [];
    (data.scope.portfolios || []).forEach((p) => bits.push(`Portfolio ${p.name}`));
    (data.scope.programs || []).forEach((p) => bits.push(`Program ${p.name}`));
    (data.scope.divisions || []).forEach((d) => bits.push(d.label));
    (data.scope.jobs || []).forEach((j) => bits.push(`Job ${j}`));
    return bits.join(' · ');
  }, [data]);

  const programItems = catalog.programs.map((p) => ({ id: String(p.id), label: `${p.name} (${p.memberCount})` }));
  const portfolioItems = catalog.portfolios.map((p) => ({ id: String(p.id), label: `${p.name} (${(p.childProgramIds || []).length} programs)` }));
  const divisionItems = catalog.divisions.map((d) => ({ id: String(d.id), label: d.label }));
  const jobItems = catalog.jobs.map((j) => ({ id: j.jobNumber, label: j.label }));

  const nameOf = (items, id) => items.find((i) => String(i.id) === String(id))?.label || String(id);

  const tooltipFormatter = (value, name) => [fmtH(value), name];

  return (
    <div style={{ padding: '1rem 1.25rem 5rem', maxWidth: '100%', margin: 0 }}>
      {showHelp && <PortfolioEvmHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', whiteSpace: 'nowrap' }}>Portfolio EVM</h2>
        {scopeLabel && (
          <span style={{
            fontSize: 11, fontWeight: 700, color: ACCENT, background: '#ccfbf1', border: '1px solid #99f6e4',
            borderRadius: 999, padding: '2px 8px', maxWidth: '50vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={scopeLabel}>
            {scopeLabel}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button type="button" style={btn} onClick={() => setShowTable((v) => !v)}>{showTable ? '▾ Jobs' : '▸ Jobs'}</button>
          <ListenButton compact guideId={PORTFOLIO_EVM_GUIDE_ID} title="Portfolio EVM Help" accent={ACCENT} />
          <button type="button" style={btn} onClick={() => setShowHelp(true)}>❓ Help</button>
          <button
            type="button"
            style={{ ...btn, background: ACCENT, color: 'white', borderColor: ACCENT }}
            onClick={() => apply(true)}
            disabled={loading || scopeEmpty}
            title="Re-read P6 for every job in the scope"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Scope picker */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 12,
        display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap',
      }}>
        <FilterDropdown label="Portfolios" summary={pickCountSummary(scope.portfolios, portfolioItems, scope.portfolios[0] && nameOf(portfolioItems, scope.portfolios[0]))} active={!!scope.portfolios.length} popoverWidth={360} buttonWidth={180} portal zIndex={200}>
          <FilterCheckList items={portfolioItems} selectedIds={scope.portfolios} onToggle={(id) => setScope((s) => ({ ...s, portfolios: toggleIn(s.portfolios, id) }))} onSelectAll={() => setScope((s) => ({ ...s, portfolios: s.portfolios.length === portfolioItems.length ? [] : portfolioItems.map((i) => i.id) }))} empty="No portfolios saved yet — create them on the Corporation Resource Histogram." />
        </FilterDropdown>
        <FilterDropdown label="Programs" summary={pickCountSummary(scope.programs, programItems, scope.programs[0] && nameOf(programItems, scope.programs[0]))} active={!!scope.programs.length} popoverWidth={360} buttonWidth={180} portal zIndex={200}>
          <FilterCheckList items={programItems} selectedIds={scope.programs} onToggle={(id) => setScope((s) => ({ ...s, programs: toggleIn(s.programs, id) }))} onSelectAll={() => setScope((s) => ({ ...s, programs: s.programs.length === programItems.length ? [] : programItems.map((i) => i.id) }))} empty="No programs saved yet — create them on the Corporation Resource Histogram." />
        </FilterDropdown>
        <FilterDropdown label="Divisions" summary={pickCountSummary(scope.divisions, divisionItems, scope.divisions[0] && nameOf(divisionItems, scope.divisions[0]))} active={!!scope.divisions.length} popoverWidth={320} buttonWidth={180} portal zIndex={200}>
          <FilterCheckList items={divisionItems} selectedIds={scope.divisions} onToggle={(id) => setScope((s) => ({ ...s, divisions: toggleIn(s.divisions, id) }))} onSelectAll={() => setScope((s) => ({ ...s, divisions: s.divisions.length === divisionItems.length ? [] : divisionItems.map((i) => i.id) }))} search={false} />
        </FilterDropdown>
        <FilterDropdown label="Jobs" summary={pickCountSummary(scope.jobs, jobItems, scope.jobs[0])} active={!!scope.jobs.length} popoverWidth={620} buttonWidth={180} portal zIndex={200}>
          <FilterCheckList items={jobItems} selectedIds={scope.jobs} onToggle={(id) => setScope((s) => ({ ...s, jobs: toggleIn(s.jobs, id) }))} onSelectAll={() => setScope((s) => ({ ...s, jobs: s.jobs.length === jobItems.length ? [] : jobItems.map((i) => i.id) }))} />
        </FilterDropdown>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: '#334155', fontWeight: 600, height: 24 }}>
          <input type="checkbox" checked={scope.includeUnawarded} onChange={(e) => setScope((s) => ({ ...s, includeUnawarded: e.target.checked }))} style={{ accentColor: ACCENT }} />
          Include unawarded estimates
        </label>
        <button type="button" style={{ ...btn, background: ACCENT, color: 'white', borderColor: ACCENT, height: 28 }} onClick={() => apply(false)} disabled={loading || scopeEmpty}>
          {loading ? 'Loading…' : 'Apply'}
        </button>
        <button type="button" style={{ ...btn, height: 28 }} onClick={() => setScope({ jobs: [], programs: [], portfolios: [], divisions: [], includeUnawarded: false })} disabled={scopeEmpty}>
          Clear
        </button>
        {catalogError && <span style={{ fontSize: '0.74rem', color: '#b91c1c' }}>{catalogError}</span>}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {!data && !loading && !error && (
        <div style={{ color: '#64748b', fontSize: '0.9rem', padding: '2rem 0', textAlign: 'center' }}>
          Pick a portfolio, a program, a division, or jobs — any mix — then press Apply.
        </div>
      )}

      {loading && !data && (
        <div style={{ color: '#64748b', fontSize: '0.9rem', padding: '2rem 0', textAlign: 'center' }}>
          Reading P6 and Maestro for every job in the scope… the first read of a job takes a few seconds.
        </div>
      )}

      {data && pmi && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 8, marginBottom: 12, opacity: loading ? 0.6 : 1 }}>
            <KpiCard label="BAC" value={fmtH(pmi.bac)} sub="budget at completion, h" />
            <KpiCard label="PV" value={fmtH(pmi.pv)} sub="planned to data date" />
            <KpiCard label="EV" value={fmtH(pmi.ev)} sub={`${fmtPct(pmi.pct_complete)} complete`} />
            <KpiCard label="AC" value={fmtH(pmi.ac)} sub={`${fmtPct(pmi.pct_spent)} of BAC spent`} />
            <KpiCard label="SV" value={fmtSigned(pmi.sv)} sub={pmi.sv_pct != null ? `${pmi.sv_pct > 0 ? '+' : ''}${pmi.sv_pct}%` : ''} color={pmi.sv != null && pmi.sv < 0 ? '#b91c1c' : '#15803d'} />
            <KpiCard label="CV" value={fmtSigned(pmi.cv)} sub={pmi.cv_pct != null ? `${pmi.cv_pct > 0 ? '+' : ''}${pmi.cv_pct}%` : ''} color={pmi.cv != null && pmi.cv < 0 ? '#b91c1c' : '#15803d'} />
            <KpiCard label="SPI" value={fmtIdx(pmi.spi)} sub="EV ÷ PV from the sums" color={indexColor(pmi.spi)} />
            <KpiCard label="CPI" value={fmtIdx(pmi.cpi)} sub="EV ÷ AC from the sums" color={indexColor(pmi.cpi)} />
            <KpiCard label="EAC" value={fmtH(pmi.eac)} sub={pmi.eac_method === 'sum_of_member_eac' ? 'sum of each job’s EAC' : 'BAC ÷ CPI'} title={`BAC ÷ blended CPI would be ${fmtH(pmi.fac_cpi)}`} />
            <KpiCard label="ETC" value={fmtH(pmi.etc)} sub="EAC − AC" />
            <KpiCard label="VAC" value={fmtSigned(pmi.vac)} sub="BAC − EAC" color={pmi.vac != null && pmi.vac < 0 ? '#b91c1c' : '#15803d'} />
            <KpiCard label="TCPI" value={fmtIdx(pmi.tcpi_bac)} sub={`to EAC ${fmtIdx(pmi.tcpi_eac)}`} />
            <KpiCard label="Data date" value={fmtDate(pmi.data_date) || '—'} sub={closedWeek ? `closed week ${fmtDate(closedWeek)}` : ''} />
            <KpiCard
              label="Jobs"
              value={String(pmi.member_count || 0)}
              sub={[pmi.no_data_count ? `${pmi.no_data_count} no data` : null, pmi.unavailable_count ? `${pmi.unavailable_count} unavailable` : null, data.scope?.skipped?.length ? `${data.scope.skipped.length} unawarded` : null].filter(Boolean).join(' · ') || 'all with EVM data'}
              color={pmi.unavailable_count ? '#b45309' : undefined}
            />
          </div>

          {data.meta?.mixedDataDates && (
            <div style={{ fontSize: '0.74rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
              The jobs in this scope have different P6 data dates. The headline uses the latest one; each job’s own date is in the table.
            </div>
          )}

          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px 4px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>Weekly hours by job</div>
              <FilterDropdown label="Bars" summary={SERIES[series].label} buttonWidth={140} popoverWidth={200} portal zIndex={200} active>
                <FilterRadioList name="portfolio-evm-series" search={false} value={series} onChange={setSeries} groups={[{ items: Object.entries(SERIES).map(([id, s]) => ({ id, label: s.label })) }]} />
              </FilterDropdown>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 10, fontSize: '0.7rem', color: '#475569', flexWrap: 'wrap' }}>
                <span><span style={{ display: 'inline-block', width: 18, height: 3, background: SERIES.budget.color, verticalAlign: 'middle', marginRight: 4 }} />Budget cum.</span>
                <span><span style={{ display: 'inline-block', width: 18, height: 3, background: SERIES.earned.color, verticalAlign: 'middle', marginRight: 4 }} />Earned cum.</span>
                <span><span style={{ display: 'inline-block', width: 18, height: 3, background: SERIES.actual.color, verticalAlign: 'middle', marginRight: 4 }} />Spent cum.</span>
                <span><span style={{ display: 'inline-block', width: 18, height: 3, background: SERIES.remaining.color, verticalAlign: 'middle', marginRight: 4, borderTop: '1px dashed #fff' }} />Forecast cum.</span>
              </div>
            </div>
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <ComposedChart data={chartRows} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: '#64748b' }} minTickGap={28} />
                  <YAxis yAxisId="bars" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={fmtH} width={56} />
                  <YAxis yAxisId="cum" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={fmtH} width={62} />
                  <Tooltip formatter={tooltipFormatter} labelFormatter={(d) => `Week of ${fmtDate(d)}`} contentStyle={{ fontSize: 11 }} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
                    onClick={(e) => {
                      const key = e?.dataKey || e?.payload?.dataKey;
                      if (!key || !String(key).includes(':')) return;
                      setHidden((h) => { const n = new Set(h); if (n.has(key)) n.delete(key); else n.add(key); return n; });
                    }}
                  />
                  {okMembers.map((m) => (
                    <Bar
                      key={m.key}
                      yAxisId="bars"
                      dataKey={m.key}
                      name={m.jobNumber || m.token}
                      stackId="members"
                      fill={memberColor[m.key]}
                      hide={hidden.has(m.key)}
                      isAnimationActive={false}
                    />
                  ))}
                  <Line yAxisId="cum" type="monotone" dataKey="budget_cumulative" name="Budget cum." legendType="none" stroke={SERIES.budget.color} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                  <Line yAxisId="cum" type="monotone" dataKey="earned_cumulative" name="Earned cum." legendType="none" stroke={SERIES.earned.color} strokeWidth={2.2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="cum" type="monotone" dataKey="actual_cumulative" name="Spent cum." legendType="none" stroke={SERIES.actual.color} strokeWidth={2.2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="cum" type="monotone" dataKey="forecast_cumulative" name="Forecast cum." legendType="none" stroke={SERIES.remaining.color} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                  {closedWeek && <ReferenceLine yAxisId="bars" x={closedWeek} stroke="#0f172a" strokeDasharray="3 3" label={{ value: `closed ${fmtDate(closedWeek)}`, position: 'insideTopRight', fontSize: 10, fill: '#0f172a' }} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {showTable && (
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left' }}>
                    {['', 'Job', 'Name', 'Came in via', 'Data date', 'BAC', 'PV', 'EV', 'AC', 'EAC', 'SPI', 'CPI', '% done', 'Notes'].map((h, i) => (
                      <th key={h || i} style={{ padding: '7px 8px', fontWeight: 700, whiteSpace: 'nowrap', textAlign: i >= 5 && i <= 12 ? 'right' : 'left', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...okMembers].sort((a, b) => (b.pmi?.bac || 0) - (a.pmi?.bac || 0)).map((m) => (
                    <tr key={m.key} style={{ borderBottom: '1px solid #f1f5f9', opacity: hidden.has(m.key) ? 0.45 : 1 }}>
                      <td style={{ padding: '6px 8px' }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: memberColor[m.key] }} /></td>
                      <td style={{ padding: '6px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>{m.jobNumber || m.token}{m.estimateNumber && m.estimateNumber !== m.jobNumber ? <span style={{ color: '#64748b', fontWeight: 400 }}> · {m.estimateNumber}</span> : null}</td>
                      <td style={{ padding: '6px 8px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={[m.clientName, m.jobName].filter(Boolean).join(' · ')}>{[m.clientName, m.jobName].filter(Boolean).join(' · ')}</td>
                      <td style={{ padding: '6px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>{(m.via || []).map((v) => v.replace(/^(program|portfolio|division):/, '')).join(', ')}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDate(m.dataDate)}{m.flags?.includes('stale_data_date') ? <span title={FLAG_TEXT.stale_data_date} style={{ color: '#b45309', marginLeft: 4 }}>⚠</span> : null}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(m.pmi?.bac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(m.pmi?.pv)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(m.pmi?.ev)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(m.pmi?.ac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(m.pmi?.eac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: indexColor(m.pmi?.spi) }}>{fmtIdx(m.pmi?.spi)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: indexColor(m.pmi?.cpi) }}>{fmtIdx(m.pmi?.cpi)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtPct(m.pmi?.pct_complete)}</td>
                      <td style={{ padding: '6px 8px', color: '#92400e', fontSize: '0.7rem' }}>
                        {(m.flags || []).filter((f) => f !== 'stale_data_date').map((f) => <div key={f} title={FLAG_TEXT[f] || f}>{FLAG_TEXT[f] || f}</div>)}
                      </td>
                    </tr>
                  ))}
                  {okMembers.length > 0 && (
                    <tr style={{ background: '#f8fafc', fontWeight: 800, borderTop: '2px solid #e2e8f0' }}>
                      <td />
                      <td style={{ padding: '6px 8px' }}>Scope</td>
                      <td colSpan={3} style={{ padding: '6px 8px', color: '#64748b', fontWeight: 400 }}>{pmi.member_count} jobs · sums, indices recomputed</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(pmi.bac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(pmi.pv)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(pmi.ev)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(pmi.ac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtH(pmi.eac)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: indexColor(pmi.spi) }}>{fmtIdx(pmi.spi)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: indexColor(pmi.cpi) }}>{fmtIdx(pmi.cpi)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtPct(pmi.pct_complete)}</td>
                      <td />
                    </tr>
                  )}
                  {otherMembers.map((m) => (
                    <tr key={m.key} style={{ borderBottom: '1px solid #f1f5f9', color: '#94a3b8' }}>
                      <td />
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{m.jobNumber || m.token}</td>
                      <td style={{ padding: '6px 8px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[m.clientName, m.jobName].filter(Boolean).join(' · ')}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{(m.via || []).map((v) => v.replace(/^(program|portfolio|division):/, '')).join(', ')}</td>
                      <td colSpan={9} style={{ padding: '6px 8px', fontStyle: 'italic' }}>
                        {m.status === 'unavailable' ? `Not in the sums — ${m.error || 'P6 unavailable'}` : 'No P6 budget, earned, or Maestro spent — not counted'}
                      </td>
                      <td />
                    </tr>
                  ))}
                  {(data.scope?.skipped || []).map((s, i) => (
                    <tr key={`${s.token}-${i}`} style={{ borderBottom: '1px solid #f1f5f9', color: '#94a3b8' }}>
                      <td />
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{s.estimateNumber || s.token}</td>
                      <td style={{ padding: '6px 8px' }} />
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{String(s.via || '').replace(/^(program|portfolio|division):/, '')}</td>
                      <td colSpan={9} style={{ padding: '6px 8px', fontStyle: 'italic' }}>
                        {s.reason === 'unawarded' ? 'Unawarded estimate — tick “Include unawarded estimates” to add its budget' : 'Could not be matched to a job or estimate'}
                      </td>
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 8 }}>
            Hours. BAC, PV, EV, AC, remaining are summed across the jobs; EAC is the sum of each job’s own EAC; SPI, CPI, TCPI are recomputed from the sums.
            Each job uses the same automatic P6 picks as its own EVM page. Built {data.meta?.builtAt ? new Date(data.meta.builtAt).toLocaleTimeString('en-CA') : ''}.
          </div>
        </>
      )}
    </div>
  );
}
